import React, { useState, useEffect, useRef } from "react";
import { Palette, Check, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

/**
 * ThemeSwitcher
 * Floating pill-button in the bottom-right corner. Clicking opens a small
 * panel with available colour themes. Selection is persisted via the
 * ThemeProvider (localStorage).
 */
export default function ThemeSwitcher() {
  const { theme, setTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = themes.find((t) => t.id === theme) || themes[0];

  return (
    <div
      ref={panelRef}
      className="fixed bottom-5 right-5 lg:bottom-8 lg:right-8 z-[60]"
    >
      {/* Panel */}
      <div
        className={`absolute bottom-full right-0 mb-3 w-[260px] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/10 p-4 transition-all duration-200 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-semibold text-[14px] text-slate-900">Theme</div>
            <div className="text-[11px] text-slate-500">Pick a brand colour</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="h-7 w-7 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {themes.map((t) => {
            const isActive = t.id === theme;
            return (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`group relative rounded-xl p-2.5 border transition-all ${
                  isActive
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200 hover:border-slate-400"
                }`}
                aria-pressed={isActive}
                aria-label={t.label}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-5 w-5 rounded-full ring-2 ring-white shadow"
                    style={{ background: t.swatch }}
                  />
                  <span
                    className="h-5 w-5 rounded-full ring-2 ring-white shadow -ml-3"
                    style={{ background: t.accent }}
                  />
                </div>
                <div className={`mt-2 text-[12px] font-medium ${isActive ? "text-slate-900" : "text-slate-600"}`}>
                  {t.label}
                </div>
                {isActive && (
                  <div className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-slate-900 text-white grid place-items-center">
                    <Check className="h-3 w-3" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Floating trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="group flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-white border border-slate-200 shadow-lg shadow-slate-900/10 hover:shadow-xl hover:-translate-y-0.5 transition-all"
        aria-label="Change theme"
      >
        <span
          className="h-7 w-7 rounded-full flex-shrink-0 ring-2 ring-white shadow-inner relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${active.swatch} 50%, ${active.accent} 50%)` }}
        >
          <Palette className="absolute inset-0 m-auto h-3.5 w-3.5 text-white mix-blend-difference" />
        </span>
        <span className="font-semibold text-[13px] text-slate-800 hidden sm:block">
          Theme
        </span>
      </button>
    </div>
  );
}
